import { prepareClinicalTimes, decodeClinicalTimes } from '../clinicalTimeCodec';
import { getLaborEpisodeChronologyError, getBirthChronologyError } from '../birthChronology';
import { normalizeNewbornWeight } from '../measurementUtils';
import { resolveClinicalMutationContext, tryCommitClinicalMutation, getClinicalResource, listClinicalResources } from '../../offline/clinicalRepository';
import { newClinicalOperationId } from '../clinicalSaveResult';
import { captureClinicalTime } from '../../offline/clinicalClock';
import { applyClinicalPatch, clinicalChanges } from '../../offline/clinicalMerge';
import { listEditorRecoveries } from '../../offline/editorRecovery';

const createIntents = new WeakMap();
const editorBases = new Map();
const clone = value => JSON.parse(JSON.stringify(value || {}));
const editorKey = (context, type, ref) => `${context.clientReferenceId}:${type}:${ref}`;
const rememberRead = (context, type, result) => {
  if (result?.data) editorBases.set(editorKey(context, type, result.data.referenceId || result.referenceId || context.resourceReferenceId), clone(result.data));
};
window.addEventListener('vela:access-context-changed', () => editorBases.clear());
window.addEventListener('vela:offline-owner-logged-out', () => editorBases.clear());
window.addEventListener('vela:offline-vault-changed', () => editorBases.clear());

// The canonical store uses the casing returned by ASP.NET's JSON serializer.
// Omitted fields remain omitted; null, false, zero, and empty arrays are edits.
const canonicalKey = key => {
  if (!/^[A-Z]/.test(key)) return key;
  const chars = key.split('');
  for (let i = 0; i < chars.length; i += 1) {
    if (i === 1 && !/[A-Z]/.test(chars[i])) break;
    if (i > 0 && i + 1 < chars.length && !/[A-Z]/.test(chars[i + 1])) {
      if (chars[i + 1] === ' ') chars[i] = chars[i].toLowerCase();
      break;
    }
    chars[i] = chars[i].toLowerCase();
  }
  return chars.join('');
};
export function clinicalPayload(value, base) {
  if (Array.isArray(value)) return value.map(item => clinicalPayload(item,
    Array.isArray(base) ? base.find(existing => (existing.referenceId || existing.id) === (item?.referenceId || item?.id)) : undefined));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value)
    .filter(([key]) => !key.startsWith('__clinical') && !['clientRequestId', 'request'].includes(key))
    .map(([key, item]) => {
      const name = Object.keys(base || {}).find(existing => existing.toLowerCase() === key.toLowerCase()) || canonicalKey(key);
      return [name, ['answers', 'Answers'].includes(key) ? item : clinicalPayload(item, base?.[name])];
    }));
}

export function createIntentReference(payload = {}) {
  const existing = payload.referenceId || payload.ReferenceId;
  if (existing) return existing;
  if (!createIntents.has(payload)) {
    const operationId = payload.clientRequestId || newClinicalOperationId();
    const hex = operationId.replace(/-/g, '');
    if (!/^[a-f0-9]{32}$/i.test(hex)) throw new Error('A stable creation identity is required.');
    const bytes = hex.slice(0, 24).match(/../g).map(pair => String.fromCharCode(parseInt(pair, 16))).join('');
    createIntents.set(payload, btoa(bytes).replace(/\+/g, '-').replace(/\//g, '_'));
  }
  return createIntents.get(payload);
}

export const clinicalResultData = result => result && ({ ...decodeClinicalTimes(result.data),
  __clinicalSaveStatus: result.status, __clinicalMutationId: result.mutationId, __clinicalRevision: result.revision });

export function clinicalCaptureMetadata(payload, resourceType) {
  const times = {};
  const collect = (value, path = '') => {
    if (!value || typeof value !== 'object') return;
    for (const [key, item] of Object.entries(value)) {
      const childPath = path ? `${path}.${key}` : key;
      if (typeof item === 'string' && item && /(time|date|timestamp|at)$/i.test(key)) times[childPath] = item;
      else if (item && typeof item === 'object') collect(item, childPath);
    }
  };
  collect(payload);
  return captureClinicalTime({ clinicalTime: Object.keys(times).length ? times : null,
    source: resourceType === 'fhtSession' ? 'device-measured' : 'clinician-entered' });
}

export async function assertClinicalChartReady(input) {
  if (navigator.onLine === false) throw new Error('Reconnect and sync this chart before signing, locking, voiding, or discharging it.');
  const context = await resolveClinicalMutationContext(input);
  if (context) {
    const resources = await listClinicalResources({ clientReferenceId: context.clientReferenceId });
    if (resources.some(resource => resource.status !== 'synced' || resource.mrnStatus === 'pending' || resource.clockReview)) {
      throw new Error('Sync the chart changes and resolve items needing review before completing this action.');
    }
  }
  const recoveries = await listEditorRecoveries();
  const identities = [input.resourceReferenceId, input.parentReferenceId].filter(Boolean).map(String);
  if (recoveries.some(copy => copy.unreadable
    || (context?.clientReferenceId && copy.clientReferenceId === context.clientReferenceId)
    || (context?.pregnancyReferenceId && copy.pregnancyReferenceId === context.pregnancyReferenceId)
    || identities.includes(String(copy.chartId)) || identities.some(id => copy.key?.endsWith(`:${id}`)))) {
    throw new Error('Review and resolve the chart recovery copy on this device before completing this action.');
  }
}

export async function clinicalNetworkRouteId(input, suppliedId) {
  if (/^\d+$/.test(String(suppliedId))) return suppliedId;
  const local = await readClinicalResource({ ...input, resourceReferenceId: suppliedId });
  const numeric = local?.id ?? local?.Id;
  if (numeric != null && /^\d+$/.test(String(numeric))) return numeric;
  throw new Error('Sync and refresh this visit before using this server action.');
}

export async function clinicalEditorChanges(input, value) {
  const context = await resolveClinicalMutationContext(input);
  if (!context) return clinicalPayload(value);
  const base = editorBases.get(editorKey(context, input.resourceType, context.resourceReferenceId || input.resourceReferenceId)) || context.baseValues;
  return clinicalChanges(base, clinicalPayload(value, base)) || {};
}

export async function saveClinicalResource(input) {
  // Resolve before allocating an ID: legacy online writes need no vault or
  // browser crypto, and remain available throughout a staged rollout.
  const context = await resolveClinicalMutationContext(input);
  if (!context) return null;
  const resourceReferenceId = context.resourceReferenceId || input.resourceReferenceId
    || (input.operation === 'create' ? createIntentReference(input.payload) : null);
  if (!resourceReferenceId) throw new Error('Prepare this chart again before saving this section.');
  const key = editorKey(context, input.resourceType, resourceReferenceId);
  const baseValues = input.baseValues || editorBases.get(key) || context.baseValues;
  let payload = clinicalPayload(input.payload || {}, baseValues);
  if (['laborEpisode', 'birthEvent', 'laborRecord', 'postpartumAssessment', 'resuscitation', 'resuscitationIntervention', 'hemorrhageEvent'].includes(input.resourceType)) payload = await prepareClinicalTimes(payload, baseValues);
  const resolved = decodeClinicalTimes(payload);
  const chronologyError = input.resourceType === 'laborEpisode' ? getLaborEpisodeChronologyError(resolved, { requireResolved: true })
    : input.resourceType === 'birthEvent' ? getBirthChronologyError(resolved, { requireResolved: true }) : null;
  if (chronologyError) throw new Error(chronologyError);
  if (input.resourceType === 'baby') {
    // Form-only date, score and derived unit fields have no server columns.
    // Sending them creates device-only values that can never be acknowledged.
    if (payload.weightInGrams === undefined && ['weightKg', 'weightLbs', 'weightOz'].some(key => payload[key] != null && payload[key] !== ''))
      payload.weightInGrams = normalizeNewbornWeight(payload).weightInGrams;
    if (payload.lengthInCm === undefined && payload.lengthInches !== undefined)
      payload.lengthInCm = payload.lengthInches == null || payload.lengthInches === '' ? null : Number(payload.lengthInches) * 2.54;
    // This is a command, not a stored baby field. A normalized false value is not an edit.
    if (payload.generateNewbornMRN !== true) delete payload.generateNewbornMRN;
    const fields = new Set(['referenceId', 'firstName', 'middleName', 'lastName', 'sex', 'birthDate',
      'newbornMRN', 'generateNewbornMRN', 'birthEventReferenceId', 'weightInGrams', 'lengthInCm',
      'isTwin', 'twinOrder', 'twinType', 'chorionicity']);
    payload = Object.fromEntries(Object.entries(payload).filter(([key]) => fields.has(key)));
  }
  const result = await tryCommitClinicalMutation({ ...input, ...context, resourceReferenceId, baseValues,
    payload,
    captureMetadata: input.captureMetadata || clinicalCaptureMetadata(input.payload, input.resourceType) });
  if (!result) throw new Error('Device chart access changed. Keep this form open and unlock it before saving.');
  // Track what this editor submitted, rather than importing another tab's
  // unseen values into the baseline for its next complete-form save.
  editorBases.set(key, clone(applyClinicalPatch(baseValues, payload)));
  return clinicalResultData(result);
}

export async function readClinicalResource(input) {
  const context = await resolveClinicalMutationContext(input);
  if (!context?.resourceReferenceId) return null;
  const result = await getClinicalResource({ ...input, ...context });
  rememberRead(context, input.resourceType, result);
  if (result?.data?.deleted) throw Object.assign(new Error('This record was removed on this device.'), { status: 404 });
  return clinicalResultData(result);
}

export async function readClinicalResourceList(input) {
  const context = await resolveClinicalMutationContext(input);
  if (!context) return null;
  const result = await listClinicalResources({ ...input, ...context });
  for (const item of result) rememberRead(context, input.resourceType, item);
  return result.filter(item => !item.data?.deleted).map(clinicalResultData);
}
